import { Stack, Modal, MessageBar, MessageBarType, IconButton } from "@fluentui/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { useNavigate } from "react-router";
import { useAppContext } from "./AppContext";
import ReceiptImage from "./ReceiptImage";
import JsonView from '@uiw/react-json-view';
import ProgressBar from "./ProgressBar";
import ReceiptSummary from "./ReceiptSummary";
import React from 'react';
import SConsciousApiService from "../services/SConsciousApiService";
import { IReport_Merchant } from "../../../shared/interfaces/IReceipAnalyzerEntities";

const formatScanErrorForUi = (raw: string): { title: string; detail: string } => {
    const fallback = {
        title: "This receipt could not be scanned",
        detail: "Please try again with a clearer photo, better lighting, and the full receipt visible.",
    };
    if (!raw) return fallback;

    // Remove technical pipeline tags like [ai-ocr-xiaomi] / [legacy-azure]
    const cleaned = raw.replace(/^\[[^\]]+\]\s*/i, "").trim();
    const lower = cleaned.toLowerCase();

    if (lower.includes("no receipt has been found")) {
        return {
            title: "No receipt found",
            detail: "This image does not appear to contain a receipt. Please scan a photo of a receipt from a store, restaurant, or service provider.",
        };
    }

    if (lower.includes("no valid receipt data") || lower.includes("no receipt")) {
        return {
            title: "We couldn't read a valid receipt",
            detail: "Try retaking the photo with the entire receipt in frame and text clearly visible.",
        };
    }

    if (lower.includes("reconciliation") || lower.includes("total") || lower.includes("mismatch")) {
        return {
            title: "We couldn't fully reconcile this receipt",
            detail: "The line-item total did not match the receipt total. Please review this receipt or scan again.",
        };
    }

    if (lower.includes("failed at") || lower.includes("after retries")) {
        return {
            title: "Receipt scan failed",
            detail: "The analysis service did not complete in time. Please try again in a moment.",
        };
    }

    // Keep custom server detail if present, but avoid exposing bracketed pipeline internals.
    return {
        title: "This receipt could not be scanned",
        detail: cleaned,
    };
};

export default function Receipt({setImgSrc}: any) {
    // Import services
    let _sConsciousApiService = new SConsciousApiService();

    //Variables
    const {appData, setAppData} = useAppContext(); // <-- get context method
    const [showCodeModal, setShowCodeModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [loading, setLoading] = useState<string>("");
    const navigate = useNavigate();    
    const [searchParams] = useSearchParams();
    const [message, setMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
    const receipt_id = searchParams.get("receiptId");
    const blobName = searchParams.get("blobName");
    const scanError = decodeURIComponent(searchParams.get("scanError") || "");
    const scanErrorUi = formatScanErrorForUi(scanError);
    // Compute the failed-scan image URL synchronously — no async fetch needed.
    const failedImageData = (blobName && appData?.user?.userId)
        ? `/api/getImage?userId=${encodeURIComponent(appData.user.userId)}&blobName=${encodeURIComponent(blobName)}`
        : '';
    const [confirmDeleteBlob, setConfirmDeleteBlob] = useState(false);
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [rescanning, setRescanning] = useState(false);

    useEffect(() => {
        if (appData?.user?.userId) _sConsciousApiService.adminCheck(appData.user.userId).then(setIsAdmin);
    }, [appData?.user?.userId]);

    const rescanCount: number = appData?.analysisResult?.receipt?.rescan_count || 0;
    const canRescan = isAdmin || rescanCount < 1;

    const handleRescan = async () => {
        if (!canRescan || rescanning) return;
        const receiptId = appData?.analysisResult?.receipt?.receipt_id;
        const userId = appData?.user?.userId;
        if (!receiptId || !userId) return;
        setRescanning(true);
        setMessage(null);
        const result = await _sConsciousApiService.rescanReceipt(receiptId, userId);
        setRescanning(false);
        if (result?.error) {
            setMessage({ type: MessageBarType.error, text: result.error });
        } else {
            setAppData({ ...appData, analysisResult: undefined, loading: true });
            navigate('/', { replace: true });
        }
    };

    //console.log("AppData.analysisResult", appData?.analysisResult);

    let formatted = ""
     useEffect(() => {
        
        try {
            //const parsed = JSON.parse(receiptData.analysisResult);
            formatted = JSON.stringify(appData?.analysisResult, null, 2); // Indent with 2 spaces
        } catch (err) {
            formatted = "Invalid JSON";
        }

    }, [appData?.analysisResult]);

    useEffect(() => {

        //setLoading("Getting receipt data");

        const existingReceipt = appData?.data?.reportReceipts?.find((receipt) => receipt.receipt_id === receipt_id);
        if (existingReceipt) {      
            // If receipt already exists, set it in analysisResult
            const merchant = appData.data.reportMerchants?.find((m) => m.merchant_id === existingReceipt.merchant_id);
            const productItems = appData?.data?.reportProductItems?.filter((item) => item.receipt_id === existingReceipt.receipt_id);

            setAppData({
                ...appData,
                analysisResult: {
                    receipt: existingReceipt,
                    merchant: merchant ?? {} as IReport_Merchant,
                    items: productItems ?? [],
                }
            });
        }

        //setLoading("");

    }, [receipt_id, appData.dataFetched]);

    const existingReceipt = appData?.data?.reportReceipts?.find((receipt) => receipt.receipt_id === receipt_id);
    if (appData.dataFetched && receipt_id && !blobName && !existingReceipt) {
        navigate('/');
        return null;
    }

    const deleteFailedReceipt = async () => {
        if (!blobName) return;
        const userId = appData.user.userId;
        await _sConsciousApiService.deleteReceiptBlob(userId, blobName);
        // Clean up the chat message sent to the self-conversation
        const selfConvId = [userId, userId].sort().join('_');
        const msgs = await _sConsciousApiService.getChatMessages(selfConvId, userId);
        const scanMsg = msgs.find((m: any) => m.attachmentName === 'receipt-failed:' + encodeURIComponent(blobName));
        if (scanMsg) await _sConsciousApiService.deleteChatMessage(scanMsg.messageId, userId);
        navigate(-1);
    };

    // Delete all related data and blob
    const deleteReceiptAndData = async () => {
        setLoading("Deleting receipt and data");
        setMessage(null);
        if (!appData?.analysisResult?.receipt?.receipt_id) return;

        const receipt_id = appData.analysisResult.receipt.receipt_id;
        const user_id = appData?.user.userId;
        const documentUrl = appData?.analysisResult.receipt.document_url;
        const blobName = documentUrl.split("/").pop();

        if (!blobName) return;

        await _sConsciousApiService.deleteReceiptData(receipt_id, user_id, blobName).then(() => {
            setLoading("");
            setMessage({ type: MessageBarType.success, text: "Receipt and data deleted successfully." });
        }).catch((error) => {
            setLoading("");
            setMessage({ type: MessageBarType.error, text: "Error deleting receipt and data." });
        });

        //Load data
        setAppData({
            ...appData,
            loading: true
        });

        navigate(`/Merchant?merchantId=${appData?.analysisResult?.receipt?.merchant_id}`);

    }

    if (blobName) {
        return (
            <Stack style={{width:'100%'}} horizontalAlign="center" tokens={{childrenGap: 16}}>
                {lightboxUrl && (
                    <div
                        onClick={() => setLightboxUrl(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}
                    >
                        <img
                            src={lightboxUrl}
                            alt="receipt"
                            style={{ maxWidth: '100%', maxHeight: '92%', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', cursor: 'default' }}
                            onClick={e => e.stopPropagation()}
                        />
                        <button className="closeBtn" style={{ position: 'absolute', top: 16, right: 16, zIndex: 301 }} onClick={() => setLightboxUrl(null)}>✕</button>
                    </div>
                )}
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '24px auto 0', overflow: 'hidden', position: 'relative' }}>
                    {failedImageData ? (
                        <img src={failedImageData} alt="Receipt" style={{ width: '100%', display: 'block', cursor: 'zoom-in' }} onClick={() => setLightboxUrl(failedImageData)} />
                    ) : (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4, fontSize: '0.9rem' }}>Loading image…</div>
                    )}
                    <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                        {confirmDeleteBlob ? (
                            <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(0,0,0,0.7)', borderRadius: 20, padding: '4px 8px' }}>
                                <IconButton
                                    iconProps={{ iconName: 'CheckMark' }}
                                    title="Yes, delete"
                                    ariaLabel="Yes, delete"
                                    onClick={() => { setConfirmDeleteBlob(false); deleteFailedReceipt(); }}
                                    styles={{ root: { backgroundColor: '#e53935', color: '#fff', borderRadius: '50%', width: 28, height: 28 }, rootHovered: { backgroundColor: '#c62828', color: '#fff' }, rootPressed: { backgroundColor: '#b71c1c', color: '#fff' } }}
                                />
                                <IconButton
                                    iconProps={{ iconName: 'Cancel' }}
                                    title="Cancel"
                                    ariaLabel="Cancel"
                                    onClick={() => setConfirmDeleteBlob(false)}
                                    styles={{ root: { backgroundColor: '#ffcc00', color: '#111', borderRadius: '50%', width: 38, height: 38, boxShadow: '0 2px 12px rgba(0,0,0,0.35)' }, rootHovered: { backgroundColor: '#e6b800', color: '#111' }, rootPressed: { backgroundColor: '#ccaa00', color: '#111' } }}
                                />
                            </div>
                        ) : (
                            <IconButton
                                iconProps={{ iconName: 'RecycleBin' }}
                                title="Delete Receipt"
                                ariaLabel="Delete Receipt"
                                onClick={() => setConfirmDeleteBlob(true)}
                                styles={{ root: { backgroundColor: '#ffcc00', color: '#000', borderRadius: '50%' }, rootHovered: { backgroundColor: '#e6b800', color: '#000' }, rootPressed: { backgroundColor: '#ccaa00', color: '#000' } }}
                            />
                        )}
                    </div>
                </div>
                {scanError && (
                    <div style={{padding: '12px 16px', width: '90%', margin: '0 auto' }}>
                        <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.9rem' }}>{scanErrorUi.title}</p>
                        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.75 }}>{scanErrorUi.detail}</p>
                    </div>
                )}
            </Stack>
        );
    }

    return ( 

        !appData?.analysisResult ?

            <ProgressBar label={loading} progress={20} />

        :

            <>
                {lightboxUrl && (
                    <div
                        onClick={() => setLightboxUrl(null)}
                        style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'zoom-out' }}
                    >
                        <img
                            src={lightboxUrl}
                            alt="receipt"
                            style={{ maxWidth: '100%', maxHeight: '92%', borderRadius: 10, objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)', cursor: 'default' }}
                            onClick={e => e.stopPropagation()}
                        />
                        <button className="closeBtn" style={{ position: 'absolute', top: 16, right: 16, zIndex: 301 }} onClick={() => setLightboxUrl(null)}>✕</button>
                    </div>
                )}
                <Stack style={{width:'100%'}} horizontalAlign="center" gap={5}>
                    {message && (
                        <div style={{ position: 'fixed', left: 0, bottom: 0, width: '100%', zIndex: 1000 }}>
                            <MessageBar
                                messageBarType={message.type}
                                isMultiline={false}
                                onDismiss={() => setMessage(null)}
                                dismissButtonAriaLabel="Close"
                            >
                                {message.text}
                            </MessageBar>
                        </div>
                    )}
                    {!loading && (
                        <>
                            <ReceiptSummary
                                    analysisResult={appData?.analysisResult}
                                    userId={appData?.user?.userId}
                                    onReanalyzed={(result) => setAppData({ ...appData, analysisResult: result, loading: true })}
                                />
                            <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto 24px', overflow: 'hidden' }}>
                                <ReceiptImage
                                imgString={setImgSrc}
                                imageUrl={appData?.analysisResult?.receipt?.document_url}
                                onClick={(dataUrl) => setLightboxUrl(dataUrl)}
                                sourceUserId={(appData?.analysisResult?.receipt as any)?.source_user_id}
                                receiptId={appData?.analysisResult?.receipt?.receipt_id}
                            >
                                {!(appData?.analysisResult?.receipt as any)?.source_receipt_id && (
                                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, display: 'flex', gap: 4 }}>
                                    <IconButton
                                        iconProps={{ iconName: 'Code' }}
                                        title="View JSON"
                                        ariaLabel="View JSON"
                                        onClick={() => setShowCodeModal(true)}
                                        styles={{
                                            root: {
                                                backgroundColor: '#ffcc00',
                                                color: '#000',
                                                borderRadius: '50%',
                                            },
                                            rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                            rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                        }}
                                    />
                                    {/* <IconButton
                                        iconProps={{ iconName: 'Refresh' }}
                                        title={rescanning ? 'Rescanning…' : canRescan ? 'Rescan receipt' : 'Rescan already used'}
                                        ariaLabel="Rescan receipt"
                                        disabled={!canRescan || rescanning}
                                        onClick={handleRescan}
                                        styles={{
                                            root: {
                                                backgroundColor: canRescan ? '#ffcc00' : 'rgba(0,0,0,0.15)',
                                                color: canRescan ? '#000' : 'rgba(0,0,0,0.35)',
                                                borderRadius: '50%',
                                            },
                                            rootHovered: canRescan ? { backgroundColor: '#e6b800', color: '#000' } : { backgroundColor: 'rgba(0,0,0,0.15)', color: 'rgba(0,0,0,0.35)' },
                                            rootPressed: canRescan ? { backgroundColor: '#ccaa00', color: '#000' } : { backgroundColor: 'rgba(0,0,0,0.15)', color: 'rgba(0,0,0,0.35)' },
                                            rootDisabled: { backgroundColor: 'rgba(0,0,0,0.15)', color: 'rgba(0,0,0,0.35)' },
                                        }}
                                    /> */}
                                </div>
                                )}
                                {!(appData?.analysisResult?.receipt as any)?.source_receipt_id && (
                                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                                    {confirmDelete ? (
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', background: 'rgba(0,0,0,0.7)', borderRadius: 20, padding: '4px 8px' }}>
                                            <IconButton
                                                iconProps={{ iconName: 'CheckMark' }}
                                                title="Yes, delete"
                                                ariaLabel="Yes, delete"
                                                onClick={() => { setConfirmDelete(false); deleteReceiptAndData(); }}
                                                styles={{ root: { backgroundColor: '#e53935', color: '#fff', borderRadius: '50%', width: 28, height: 28 }, rootHovered: { backgroundColor: '#c62828', color: '#fff' }, rootPressed: { backgroundColor: '#b71c1c', color: '#fff' } }}
                                            />
                                            <IconButton
                                                iconProps={{ iconName: 'Cancel' }}
                                                title="Cancel"
                                                ariaLabel="Cancel"
                                                onClick={() => setConfirmDelete(false)}
                                                styles={{ root: { backgroundColor: '#ffcc00', color: '#111', borderRadius: '50%', width: 38, height: 38, boxShadow: '0 2px 12px rgba(0,0,0,0.35)' }, rootHovered: { backgroundColor: '#e6b800', color: '#111' }, rootPressed: { backgroundColor: '#ccaa00', color: '#111' } }}
                                            />
                                        </div>
                                    ) : (
                                        <IconButton
                                            iconProps={{ iconName: 'RecycleBin' }}
                                            title="Delete Receipt"
                                            ariaLabel="Delete Receipt"
                                            onClick={() => setConfirmDelete(true)}
                                            styles={{
                                                root: {
                                                    backgroundColor: '#ffcc00',
                                                    color: '#000',
                                                    borderRadius: '50%',
                                                },
                                                rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                                rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                            }}
                                        />
                                    )}
                                </div>
                                )}
                            </ReceiptImage>
                            </div>
                        </>
                    )}
                    
                    <Modal
                        isOpen={showCodeModal}
                        onDismiss={() => setShowCodeModal(false)}
                        isBlocking={false}
                        className="fleuntui-modal"
                        styles={{ main: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: 32, overflow: 'hidden' } }}
                    >
                        {appData?.analysisResult && (
                            <JsonView value={appData?.analysisResult} />
                        )}
                    </Modal>
                </Stack>
                <br />
            </>
    )
}